import { useEffect, useRef, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import QRCode from "qrcode"
import { Loader2, Copy, ShieldCheck, Download, Check } from "lucide-react"
import { authApi, ApiError } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

type Props = {
  enrolId: string
  secret: string
  uri: string
  email: string
  onSuccess: (csrf: string) => void
}

type Stage = "scan" | "verify" | "codes"

export function TotpEnrolFlow({ enrolId, secret, uri, email, onSuccess }: Props) {
  const [stage, setStage] = useState<Stage>("scan")
  const [code, setCode] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [copiedCodes, setCopiedCodes] = useState(false)
  const [csrf, setCsrf] = useState<string | null>(null)
  const qrCanvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (qrCanvasRef.current && stage === "scan") {
      QRCode.toCanvas(qrCanvasRef.current, uri, {
        width: 220,
        margin: 1,
        color: { dark: "#0f172a", light: "#ffffff" },
      }).catch(console.error)
    }
  }, [stage, uri])

  const confirm = useMutation({
    mutationFn: () => authApi.confirmEnrolTotp(enrolId, code),
    onSuccess: (data) => {
      setRecoveryCodes(data.recoveryCodes)
      setCsrf(data.csrf)
      setStage("codes")
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Verification failed")
      setCode("")
    },
  })

  const copySecret = () => {
    navigator.clipboard.writeText(secret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 2000)
  }

  const copyRecoveryCodes = () => {
    navigator.clipboard.writeText(recoveryCodes.join("\n"))
    setCopiedCodes(true)
    setTimeout(() => setCopiedCodes(false), 2000)
  }

  const downloadRecoveryCodes = () => {
    const content = [
      "Rivercity CRM — Recovery Codes",
      `Account: ${email}`,
      `Generated: ${new Date().toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })} (Brisbane time)`,
      "",
      "Keep these codes somewhere safe. Each code can be used ONCE to sign in",
      "if you lose access to your authenticator app.",
      "",
      ...recoveryCodes.map((c, i) => `${(i + 1).toString().padStart(2, " ")}. ${c}`),
    ].join("\n")
    const blob = new Blob([content], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = "rivercity-crm-recovery-codes.txt"
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="text-white">
      {stage === "scan" && (
        <>
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-rc-blue-500/20 text-rc-blue-300 ring-1 ring-rc-blue-400/30">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-white">Set up two-factor auth</h2>
            <p className="mt-1 text-sm text-white/60">
              Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password,
              etc.)
            </p>
          </div>

          <div className="mb-6 flex justify-center">
            <div className="rounded-lg bg-white p-3 shadow-lg ring-1 ring-white/10">
              <canvas ref={qrCanvasRef} />
            </div>
          </div>

          <div className="mb-6 space-y-2">
            <Label>Can't scan? Enter this key manually:</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded-md bg-white/10 px-3 py-2 font-mono text-xs text-white ring-1 ring-white/10">
                {secret}
              </code>
              <Button
                variant="outline"
                size="icon"
                type="button"
                onClick={copySecret}
                className="border-white/20 bg-white/5 text-white hover:bg-white/10"
              >
                {copiedSecret ? (
                  <Check className="h-4 w-4 text-emerald-400" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          <Button
            className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
            onClick={() => setStage("verify")}
          >
            I've added it — continue
          </Button>
        </>
      )}

      {stage === "verify" && (
        <>
          <div className="mb-6 text-center">
            <h2 className="text-lg font-semibold text-white">Verify your authenticator</h2>
            <p className="mt-1 text-sm text-white/60">
              Enter the 6-digit code shown in your app to confirm setup.
            </p>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              setError(null)
              confirm.mutate()
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="code">Authenticator code</Label>
              <Input
                id="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="text-center font-mono text-2xl tracking-[0.5em]"
                required
                autoFocus
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-400/40 bg-red-500/10 p-3 text-sm text-red-200">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
              disabled={confirm.isPending || code.length !== 6}
            >
              {confirm.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Confirm & sign in"
              )}
            </Button>
            <button
              type="button"
              onClick={() => setStage("scan")}
              className="w-full text-center text-sm text-white/50 transition-colors hover:text-white"
            >
              ← Back
            </button>
          </form>
        </>
      )}

      {stage === "codes" && (
        <>
          <div className="mb-6 text-center">
            <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/30">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h2 className="text-lg font-semibold text-white">Save your recovery codes</h2>
            <p className="mt-1 text-sm text-white/60">
              Store these somewhere safe. If you lose access to your authenticator, you can use one
              of these codes to sign in.{" "}
              <strong className="text-white">This is the only time they will be shown.</strong>
            </p>
          </div>

          <div className="mb-4 grid grid-cols-2 gap-2 rounded-md bg-white/5 p-4 ring-1 ring-white/10">
            {recoveryCodes.map((c) => (
              <code
                key={c}
                className="rounded bg-white/10 px-2 py-1.5 font-mono text-xs text-white"
              >
                {c}
              </code>
            ))}
          </div>

          <div className="mb-6 flex gap-2">
            <Button
              variant="outline"
              className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/10"
              onClick={copyRecoveryCodes}
            >
              {copiedCodes ? (
                <Check className="h-4 w-4 text-emerald-400" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
              Copy
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-white/20 bg-white/5 text-white hover:bg-white/10"
              onClick={downloadRecoveryCodes}
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          </div>

          <Button
            className="h-11 w-full bg-gradient-to-r from-rc-blue-600 to-cyan-500 text-base font-medium text-white shadow-lg shadow-blue-900/40 hover:from-rc-blue-500 hover:to-cyan-400"
            onClick={() => csrf && onSuccess(csrf)}
          >
            I've saved them — continue to CRM
          </Button>
        </>
      )}
    </div>
  )
}
